import { useEffect, useId, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import { saveIcon } from '@progress/kendo-svg-icons';

interface ConfirmDialogProps {
    title: string;
    message: string;
    confirmLabel: string;
    onConfirm: () => void;
    onCancel: () => void;
    danger?: boolean;
}

export function ConfirmDialog({ title, message, confirmLabel, onConfirm, onCancel, danger = false }: ConfirmDialogProps) {
    const { t } = useTranslation();
    const titleId = useId();
    const messageId = useId();
    const box = useRef<HTMLDivElement>(null);
    const cancel = useRef<HTMLButtonElement>(null);
    const cancelAction = useRef(onCancel);
    cancelAction.current = onCancel;

    useEffect(() => {
        const previous = document.activeElement as HTMLElement | null;
        cancel.current?.focus();
        const keyboard = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                cancelAction.current();
            } else if (event.key === 'Tab' && box.current) {
                const controls = Array.from(box.current.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex="0"]'));
                const first = controls[0];
                const last = controls[controls.length - 1];
                if (first && (event.shiftKey && document.activeElement === first || !event.shiftKey && document.activeElement === last || !box.current.contains(document.activeElement))) {
                    event.preventDefault();
                    (event.shiftKey ? last : first)?.focus();
                }
            }
        };
        document.addEventListener('keydown', keyboard);
        return () => {
            document.removeEventListener('keydown', keyboard);
            if (previous?.isConnected) previous.focus();
        };
    }, []);

    return (
        <div className="confirm-overlay">
            <button type="button" className="confirm-backdrop" tabIndex={-1} aria-label={t('dlpConfiguration.closeDialog')} onClick={onCancel} />
            <div ref={box} className="confirm-box" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={messageId}>
                <div id={titleId} className="confirm-title">{title}</div>
                <div id={messageId} className="confirm-message">{message}</div>
                <div className="confirm-actions">
                    <button ref={cancel} type="button" className="lm-btn-secondary sm" onClick={onCancel}><Trans>general.cancel</Trans></button>
                    <button type="button" className={danger ? 'lm-btn-danger sm' : 'lm-btn-primary sm'} onClick={onConfirm}>
                        {!danger && <SvgIcon icon={saveIcon} size="small" />} {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
